import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { motion } from 'framer-motion';
import { reservationsAPI, woocommerceAPI, settingsAPI, stripeAPI } from '../../lib/api';
import { formatCurrency, formatDate, getStatusBadgeClass, getStatusLabel, downloadBlob } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  FiCalendar, FiUser, FiMail, FiPhone, FiMapPin, 
  FiCheck, FiX, FiDownload, FiDollarSign, FiFileText,
  FiPlus, FiPackage, FiImage, FiEye, FiCreditCard, FiExternalLink,
  FiMessageSquare, FiSend, FiPaperclip, FiLink, FiUsers, FiBriefcase,
  FiGift, FiTrash2, FiAlertTriangle
} from 'react-icons/fi';
import { SiStripe } from 'react-icons/si';

const API_URL = getApiUrl();

// Deposit percentage (30% of total) - fallback
const DEPOSIT_PERCENTAGE = 30;

// Default deposit settings
const depositSettings = {
  type: 'percentage',
  percentage: DEPOSIT_PERCENTAGE,
  amount: 0 // Will be calculated based on reservation total
};

export default function ReservationsPage() {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [creating, setCreating] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [savingTeam, setSavingTeam] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { user, isCollaborator } = useAuth();
  const [paymentSettings, setPaymentSettings] = useState(null);
  
  // Financial statistics
  const [financialStats, setFinancialStats] = useState({
    pending_deposits: { count: 0, amount: 0 },
    pending_balances: { count: 0, amount: 0 },
    total_pending: 0
  });

  const [newReservation, setNewReservation] = useState({
    product_id: '',
    variation_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    event_address: '',
    start_date: '',
    end_date: '',
    notes: '',
  });

  useEffect(() => {
    loadReservations();
    loadTeamMembers();
    loadFinancialStats();
    loadPaymentSettings();
  }, [statusFilter]);

  const loadReservations = async () => {
    try {
      // Handle special filters that need client-side filtering
      let apiStatus = null;
      if (statusFilter === 'awaiting_deposit') {
        apiStatus = 'approved'; // Get approved reservations, then filter for unpaid deposits
      } else if (statusFilter === 'deposit_paid') {
        apiStatus = 'approved'; // Get approved reservations, then filter for paid deposits
      } else if (statusFilter !== 'all') {
        apiStatus = statusFilter;
      }
      
      const response = await reservationsAPI.getAll(apiStatus);
      let data = response.data;
      
      // Apply client-side filtering for deposit status
      if (statusFilter === 'awaiting_deposit') {
        data = data.filter(r => r.status === 'approved' && !r.deposit_paid);
      } else if (statusFilter === 'deposit_paid') {
        data = data.filter(r => r.deposit_paid === true);
      }
      
      setReservations(data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/team-members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data);
      }
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  const loadFinancialStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/payments/mypos-ipc/stats/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFinancialStats(data);
      }
    } catch (error) {
      console.error('Error loading financial stats:', error);
    }
  };

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      // Use local products API instead of WooCommerce
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/public/products`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        // API returns {products: [...]} object, extract products array
        const productsArray = Array.isArray(data) ? data : (data.products || []);
        // Transform products to expected format and filter active ones
        const activeProducts = productsArray.filter(p => p.is_active !== false);
        setProducts(activeProducts);
      } else {
        toast.error('Erreur lors du chargement des produits');
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Load payment settings
  const loadPaymentSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/payment-settings`);
      if (response.ok) {
        const data = await response.json();
        setPaymentSettings(data);
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
    }
  };

  // Calculate deposit amount based on settings (fixed or percentage)
  const calculateDeposit = (total) => {
    // If fixed_link mode, use fixed amount
    if (paymentSettings?.deposit_type === 'fixed_link' && paymentSettings?.deposit_fixed_amount) {
      return paymentSettings.deposit_fixed_amount;
    }
    // Otherwise use percentage
    const percentage = paymentSettings?.deposit_percentage || DEPOSIT_PERCENTAGE;
    return Math.round(total * (percentage / 100) * 100) / 100;
  };

  // Calculate balance (total - deposit paid)
  const calculateBalance = (reservation) => {
    const total = reservation.total_price || reservation.total_cost || 0;
    // If deposit was paid, use the actual amount paid
    if (reservation.deposit_paid && reservation.deposit_amount_paid) {
      return Math.max(0, total - reservation.deposit_amount_paid);
    }
    // Otherwise calculate based on settings
    const depositAmount = calculateDeposit(total);
    return Math.max(0, total - depositAmount);
  };

  const handleAssignTeam = async (reservationId) => {
    try {
      setSavingTeam(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/assign-team`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ team_member_ids: selectedTeamMembers })
      });
      
      if (response.ok) {
        toast.success('Équipe assignée avec succès');
        // Update local state
        setSelectedReservation(prev => ({ ...prev, assigned_team: selectedTeamMembers }));
        loadReservations();
      } else {
        toast.error('Erreur lors de l\'assignation');
      }
    } catch (error) {
      console.error('Error assigning team:', error);
      toast.error('Erreur lors de l\'assignation');
    } finally {
      setSavingTeam(false);
    }
  };

  const handleOpenCreateDialog = () => {
    loadProducts();
    setNewReservation({
      product_id: '',
      variation_id: '',
      client_name: '',
      client_email: '',
      client_phone: '',
      event_address: '',
      start_date: '',
      end_date: '',
      notes: '',
    });
    setCreateDialogOpen(true);
  };

  const handleCreateReservation = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      // Get selected product and variation
      const product = products.find(p => String(p.id) === String(newReservation.product_id));
      const variation = product?.variations?.find(v => String(v.id) === String(newReservation.variation_id));
      
      if (!variation) {
        toast.error('Veuillez sélectionner une formule');
        setCreating(false);
        return;
      }

      const data = {
        client_name: newReservation.client_name,
        client_email: newReservation.client_email,
        client_phone: newReservation.client_phone,
        event_address: newReservation.event_address,
        start_date: newReservation.start_date,
        end_date: newReservation.end_date || newReservation.start_date,
        notes: newReservation.notes,
        // Product/Variation data
        product_id: product.id,
        product_name: `${product.name} - ${variation.name}`,
        duration_hours: variation.duration_hours || 3,
        total_price: variation.price,
        // Source
        source: 'admin',
        client_type: 'particulier', // Can be changed later
      };

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reservations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast.success('Réservation créée avec succès');
        setCreateDialogOpen(false);
        loadReservations();
        loadFinancialStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await reservationsAPI.updateStatus(id, status);
      toast.success(`Réservation ${status === 'approved' ? 'acceptée' : status === 'rejected' ? 'refusée' : 'mise à jour'}`);
      loadReservations();
      loadFinancialStats();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handlePaymentMark = async (id, method) => {
    try {
      await reservationsAPI.markPayment(id, method);
      toast.success('Paiement enregistré');
      loadReservations();
      loadFinancialStats();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // Apply commercial gesture (admin only)
  const handleGesteCommercial = async (reservationId, reason = 'Geste commercial') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/payments/mypos-ipc/reservation/${reservationId}/geste-commercial?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Geste commercial appliqué - ${formatCurrency(data.amount_waived)} de solde annulé`);
        loadReservations();
        loadFinancialStats();
        setDialogOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'application du geste commercial');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'application du geste commercial');
    }
  };

  const handleExportPDF = async (id) => {
    try {
      const response = await reservationsAPI.exportPDF(id);
      downloadBlob(response.data, `location_${id.slice(0, 8)}.pdf`);
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleExportExcel = async (id) => {
    try {
      const response = await reservationsAPI.exportExcel(id);
      downloadBlob(response.data, `location_${id.slice(0, 8)}.xlsx`);
      toast.success('Excel téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // Télécharger la fiche de réservation en PDF
  const handleDownloadPDF = async (reservation) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservation.id}/pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reservation_${reservation.client_name?.replace(/\s/g, '_') || reservation.id.slice(0, 8)}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Fiche téléchargée');
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Envoyer un design au client pour validation
  const handleSendDesign = async (reservationId, designData) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/design`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(designData)
      });
      if (response.ok) {
        toast.success('Design envoyé au client pour validation');
        loadReservations();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const openDetail = (reservation) => {
    // Navigate to full reservation detail page instead of modal
    navigate(`/dashboard/reservations/${reservation.id}`);
  };

  const handleDeleteReservation = async () => {
    if (!reservationToDelete) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reservations/${reservationToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        toast.success('Réservation supprimée avec succès');
        setDeleteDialogOpen(false);
        setReservationToDelete(null);
        loadReservations();
        loadFinancialStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Calculate pending reservations count
  const pendingReservations = reservations.filter(r => r.status === 'pending');
  const pendingDesigns = reservations.filter(r => r.design_status === 'revision_requested');
  const awaitingDeposit = reservations.filter(r => r.status === 'approved' && !r.deposit_paid);

  const selectedProduct = products.find(p => String(p.id) === String(newReservation.product_id));
  const selectedVariation = selectedProduct?.variations?.find(v => String(v.id) === String(newReservation.variation_id));

  if (loading) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="h-8 w-48 skeleton" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 bg-white rounded-xl p-6" data-testid="reservations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">Réservations</h1>
          <p className="text-sm text-slate-600 mt-1">Gérez les demandes de location</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Filtrer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="pending">En attente validation</SelectItem>
              <SelectItem value="approved">Acceptées</SelectItem>
              <SelectItem value="awaiting_deposit">En attente d'acompte</SelectItem>
              <SelectItem value="deposit_paid">Acompte payé</SelectItem>
              <SelectItem value="completed">Terminées</SelectItem>
              <SelectItem value="rejected">Refusées</SelectItem>
            </SelectContent>
          </Select>
          {!isCollaborator && (
            <Button 
              className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-full gap-2 text-sm"
              onClick={handleOpenCreateDialog}
              data-testid="create-reservation-btn"
            >
              <FiPlus className="h-4 w-4" />
              <span className="hidden sm:inline">Nouvelle réservation</span>
              <span className="sm:hidden">Nouvelle</span>
            </Button>
          )}
        </div>
      </div>

      {/* Financial Statistics Cards - Hidden for collaborators */}
      {!isCollaborator && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="financial-stats">
        <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-orange-600 uppercase tracking-wide">Acomptes en attente</p>
                <p className="text-2xl font-bold text-orange-900 mt-1">{formatCurrency(financialStats.pending_deposits?.amount || 0)}</p>
                <p className="text-xs text-orange-600 mt-1">{financialStats.pending_deposits?.count || 0} réservation{(financialStats.pending_deposits?.count || 0) > 1 ? 's' : ''}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <FiCreditCard className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Soldes à encaisser</p>
                <p className="text-2xl font-bold text-blue-900 mt-1">{formatCurrency(financialStats.pending_balances?.amount || 0)}</p>
                <p className="text-xs text-blue-600 mt-1">{financialStats.pending_balances?.count || 0} réservation{(financialStats.pending_balances?.count || 0) > 1 ? 's' : ''}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FiDollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 sm:col-span-2 lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Total à encaisser</p>
                <p className="text-2xl font-bold text-emerald-900 mt-1">{formatCurrency(financialStats.total_pending || 0)}</p>
                <p className="text-xs text-emerald-600 mt-1">Acomptes + Soldes</p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-full">
                <FiBriefcase className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Pending Reservations Banner */}
      {pendingReservations.length > 0 && statusFilter === 'all' && (
        <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <FiCalendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">
                    {pendingReservations.length} demande{pendingReservations.length > 1 ? 's' : ''} en attente de validation
                  </p>
                  <p className="text-sm text-amber-700">
                    Cliquez pour valider ou refuser ces réservations
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => setStatusFilter('pending')}
                className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
              >
                Voir les demandes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Design Revisions Banner */}
      {pendingDesigns.length > 0 && statusFilter === 'all' && (
        <Card className="border-orange-300 bg-gradient-to-r from-orange-50 to-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-full">
                  <FiImage className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold text-orange-900">
                    {pendingDesigns.length} design{pendingDesigns.length > 1 ? 's' : ''} en attente de révision
                  </p>
                  <p className="text-sm text-orange-700">
                    Des clients ont demandé des modifications sur leurs designs
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/dashboard/designs')}
                className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
              >
                Voir les designs
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Awaiting Deposit Banner */}
      {awaitingDeposit.length > 0 && statusFilter === 'all' && (
        <Card className="border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-full">
                  <FiDollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-900">
                    {awaitingDeposit.length} réservation{awaitingDeposit.length > 1 ? 's' : ''} en attente d'acompte
                  </p>
                  <p className="text-sm text-emerald-700">
                    Ces réservations sont acceptées mais l'acompte n'a pas encore été reçu
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => setStatusFilter('awaiting_deposit')}
                className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
                data-testid="filter-awaiting-deposit-btn"
              >
                <FiDollarSign className="h-4 w-4" />
                Voir les acomptes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {reservations.length === 0 ? (
        <Card className="border-slate-100">
          <CardContent className="py-16">
            <div className="empty-state">
              <div className="empty-state-icon">
                <FiCalendar />
              </div>
              <h3 className="empty-state-title">Aucune réservation</h3>
              <p className="empty-state-description">
                Les demandes de réservation apparaîtront ici.
              </p>
              <Button 
                className="mt-4 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-full"
                onClick={handleOpenCreateDialog}
              >
                <FiPlus className="mr-2" />
                Créer une réservation
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reservations.map((reservation, index) => (
            <motion.div
              key={reservation.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: index * 0.03 }}
            >
              <Card 
                className="card-hover border-slate-100 cursor-pointer" 
                onClick={() => openDetail(reservation)}
                data-testid={`reservation-card-${reservation.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      {reservation.product_image ? (
                        <img 
                          src={reservation.product_image} 
                          alt={reservation.product_name}
                          className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                          <FiPackage className="text-indigo-600 text-xl" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">
                            {reservation.product_name}
                            {reservation.variation_name && (
                              <span className="text-slate-500 font-normal"> - {reservation.variation_name}</span>
                            )}
                          </h3>
                          <span className={getStatusBadgeClass(reservation.status)}>
                            {getStatusLabel(reservation.status)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">
                          {reservation.client_name} • {reservation.client_email}
                          {reservation.client_type === 'entreprise' && (
                            <Badge className="ml-2 bg-blue-100 text-blue-800 border-blue-200 text-xs">
                              <FiBriefcase className="h-3 w-3 mr-1" />
                              {reservation.company_name || 'Entreprise'}
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatDate(reservation.start_date)} → {formatDate(reservation.end_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Statut détaillé de la réservation */}
                      <div className="flex flex-col gap-1">
                        {/* Statut du contrat */}
                        {reservation.contract_signed ? (
                          <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
                            <FiCheck className="h-3 w-3 mr-1" />
                            Contrat signé
                          </Badge>
                        ) : reservation.status === 'approved' ? (
                          <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-xs animate-pulse">
                            <FiFileText className="h-3 w-3 mr-1" />
                            En attente signature
                          </Badge>
                        ) : null}
                        
                        {/* Statut du design */}
                        {reservation.design_status === 'approved' ? (
                          <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
                            <FiImage className="h-3 w-3 mr-1" />
                            Design validé
                          </Badge>
                        ) : reservation.design_status === 'revision_requested' ? (
                          <Badge className="bg-red-100 text-red-800 border-red-200 text-xs animate-pulse">
                            <FiImage className="h-3 w-3 mr-1" />
                            Révision demandée
                          </Badge>
                        ) : reservation.design_status === 'pending' || reservation.design_sent ? (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
                            <FiImage className="h-3 w-3 mr-1" />
                            Design en attente
                          </Badge>
                        ) : reservation.status === 'approved' ? (
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                            <FiImage className="h-3 w-3 mr-1" />
                            Design à envoyer
                          </Badge>
                        ) : null}
                      </div>
                      
                      {/* Bouton Marquer Acompte Reçu - visible si approved et pas encore payé */}
                      {reservation.status === 'approved' && !reservation.deposit_paid && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePaymentMark(reservation.id, 'mypos');
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1"
                          data-testid={`mark-deposit-paid-${reservation.id}`}
                        >
                          <FiDollarSign className="h-4 w-4" />
                          <span className="hidden sm:inline">Acompte reçu</span>
                          <span className="sm:hidden">€</span>
                        </Button>
                      )}
                      
                      {/* Badge acompte payé */}
                      {reservation.deposit_paid && (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                          <FiCheck className="h-3 w-3 mr-1" />
                          Acompte payé
                        </Badge>
                      )}
                      
                      {/* Bouton Supprimer - visible uniquement pour les admins */}
                      {!isCollaborator && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReservationToDelete(reservation);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-reservation-${reservation.id}`}
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </Button>
                      )}
                      
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900">
                          {formatCurrency(reservation.total_cost || reservation.total_price || 0)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {paymentSettings?.deposit_type === 'fixed_link' 
                            ? `Acompte (fixe): ${formatCurrency(paymentSettings?.deposit_fixed_amount || 100)}`
                            : `Acompte (${paymentSettings?.deposit_percentage || 30}%): ${formatCurrency(calculateDeposit(reservation.total_cost || reservation.total_price || 0))}`
                          } | Solde: {formatCurrency((reservation.total_cost || reservation.total_price || 0) - calculateDeposit(reservation.total_cost || reservation.total_price || 0))}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Reservation Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle réservation</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateReservation} className="space-y-6">
            {/* Product Selection */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Produit</h4>
              
              <div className="space-y-2">
                <Label>Sélectionner un produit</Label>
                <Select
                  value={newReservation.product_id}
                  onValueChange={(value) => setNewReservation({ ...newReservation, product_id: value, variation_id: '' })}
                >
                  <SelectTrigger data-testid="select-product">
                    <SelectValue placeholder={loadingProducts ? "Chargement..." : "Choisir un produit"} />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={String(product.id)}>
                        <div className="flex items-center gap-2">
                          {product.images?.[0] && (
                            <img src={product.images[0]} alt="" className="w-6 h-6 rounded object-cover" />
                          )}
                          <span>{product.name}</span>
                          {product.variations?.length > 0 && (
                            <span className="text-slate-500">
                              - À partir de {formatCurrency(Math.min(...product.variations.map(v => v.price || 0)))}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Variations (Formules) */}
              {selectedProduct?.variations?.length > 0 && (
                <div className="space-y-2">
                  <Label>Formule</Label>
                  <Select
                    value={newReservation.variation_id}
                    onValueChange={(value) => setNewReservation({ ...newReservation, variation_id: value })}
                  >
                    <SelectTrigger data-testid="select-variation">
                      <SelectValue placeholder="Choisir une formule" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedProduct.variations
                        .filter(v => v.is_active !== false)
                        .map((variation) => (
                          <SelectItem key={variation.id} value={String(variation.id)}>
                            <div className="flex items-center gap-2">
                              {variation.is_popular && <span className="text-amber-500">⭐</span>}
                              <span className="font-medium">{variation.name}</span>
                              <span className="text-slate-500">
                                {variation.duration_label || (variation.duration_hours ? `${variation.duration_hours}h` : '')}
                              </span>
                              <span className="text-emerald-600 font-semibold">
                                {formatCurrency(variation.price)}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Selected Variation Summary */}
              {selectedVariation && (
                <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                        <FiPackage className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h5 className="font-semibold text-emerald-900">{selectedVariation.name}</h5>
                          {selectedVariation.is_popular && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">⭐ Populaire</Badge>
                          )}
                        </div>
                        <div className="text-sm text-emerald-800 space-y-1">
                          <p><strong>Durée :</strong> {selectedVariation.duration_label || `${selectedVariation.duration_hours}h`}</p>
                          <p><strong>Prix :</strong> {formatCurrency(selectedVariation.price)}</p>
                          <p><strong>Acompte {paymentSettings?.deposit_type === 'fixed_link' ? '(fixe)' : `(${paymentSettings?.deposit_percentage || 30}%)`} :</strong> {formatCurrency(calculateDeposit(selectedVariation.price))}</p>
                        </div>
                        {selectedVariation.features?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-emerald-700 font-medium mb-1">Inclus :</p>
                            <ul className="text-xs text-emerald-600 space-y-0.5">
                              {selectedVariation.features.slice(0, 4).map((f, i) => (
                                <li key={i} className="flex items-center gap-1">
                                  <FiCheck className="w-3 h-3" /> {f}
                                </li>
                              ))}
                              {selectedVariation.features.length > 4 && (
                                <li className="text-emerald-500 italic">+{selectedVariation.features.length - 4} autres...</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Client Info */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Informations client</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client_name">Nom complet</Label>
                  <Input
                    id="client_name"
                    value={newReservation.client_name}
                    onChange={(e) => setNewReservation({ ...newReservation, client_name: e.target.value })}
                    required
                    data-testid="client-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_phone">Téléphone</Label>
                  <Input
                    id="client_phone"
                    type="tel"
                    value={newReservation.client_phone}
                    onChange={(e) => setNewReservation({ ...newReservation, client_phone: e.target.value })}
                    required
                    data-testid="client-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_email">Email</Label>
                <Input
                  id="client_email"
                  type="email"
                  value={newReservation.client_email}
                  onChange={(e) => setNewReservation({ ...newReservation, client_email: e.target.value })}
                  required
                  data-testid="client-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="event_address">Adresse de l'événement</Label>
                <Input
                  id="event_address"
                  value={newReservation.event_address}
                  onChange={(e) => setNewReservation({ ...newReservation, event_address: e.target.value })}
                  placeholder="123 Rue de l'événement, 75001 Paris"
                  required
                  data-testid="event-address-input"
                />
              </div>
            </div>

            {/* Dates */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Période de location</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Date de début</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={newReservation.start_date}
                    onChange={(e) => setNewReservation({ ...newReservation, start_date: e.target.value })}
                    required
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">Date de fin</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={newReservation.end_date}
                    onChange={(e) => setNewReservation({ ...newReservation, end_date: e.target.value })}
                    min={newReservation.start_date}
                    required
                    data-testid="end-date-input"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optionnel)</Label>
              <Textarea
                id="notes"
                value={newReservation.notes}
                onChange={(e) => setNewReservation({ ...newReservation, notes: e.target.value })}
                placeholder="Informations complémentaires..."
                rows={3}
                data-testid="notes-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="submit"
                disabled={creating || !newReservation.product_id}
                className="bg-gradient-to-r from-indigo-600 to-violet-600"
                data-testid="submit-reservation-btn"
              >
                {creating ? 'Création...' : 'Créer la réservation'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedReservation && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>Réservation #{selectedReservation.id.slice(0, 8).toUpperCase()}</span>
                  <span className={getStatusBadgeClass(selectedReservation.status)}>
                    {getStatusLabel(selectedReservation.status)}
                  </span>
                </DialogTitle>
              </DialogHeader>

              <Tabs defaultValue="details" className="mt-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="details">Détails</TabsTrigger>
                  <TabsTrigger value="client">Client</TabsTrigger>
                  <TabsTrigger value="team">Équipe</TabsTrigger>
                  <TabsTrigger value="actions">Actions</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Produit</p>
                      <p className="font-medium">
                        {selectedReservation.product_name}
                        {selectedReservation.variation_name && (
                          <span className="text-slate-500 font-normal"> - {selectedReservation.variation_name}</span>
                        )}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Dates</p>
                      <p className="font-medium">
                        {formatDate(selectedReservation.start_date)} → {formatDate(selectedReservation.end_date)}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Adresse événement</p>
                      <p className="font-medium">{selectedReservation.event_address}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Distance</p>
                      <p className="font-medium">
                        {selectedReservation.distance_km ? `${selectedReservation.distance_km.toFixed(1)} km` : 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-4">
                    <h4 className="font-semibold text-slate-900 mb-3">Récapitulatif</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Coût location</span>
                        <span>{formatCurrency(selectedReservation.rental_cost || selectedReservation.total_price || 0)}</span>
                      </div>
                      {(selectedReservation.km_cost > 0 || selectedReservation.delivery_fee > 0) && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Frais km</span>
                          <span>{formatCurrency(selectedReservation.km_cost || selectedReservation.delivery_fee || 0)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                        <span>Total</span>
                        <span className="text-indigo-600">{formatCurrency(selectedReservation.total_cost || selectedReservation.total_price || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Acompte {paymentSettings?.deposit_type === 'fixed_link' ? '(fixe)' : `(${paymentSettings?.deposit_percentage || 30}%)`}</span>
                        <span className="flex items-center gap-2">
                          {formatCurrency(calculateDeposit(selectedReservation.total_cost || selectedReservation.total_price || 0))}
                          {selectedReservation.deposit_paid ? (
                            <span className="badge-success">Payé</span>
                          ) : (
                            <span className="badge-warning">À payer</span>
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Solde restant</span>
                        <span className="font-medium">
                          {formatCurrency(calculateBalance(selectedReservation))}
                        </span>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="client" className="space-y-4 mt-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <FiUser className="text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Nom</p>
                        <p className="font-medium">{selectedReservation.client_name}</p>
                      </div>
                    </div>
                    
                    {/* Type de client et modes de paiement */}
                    {selectedReservation.client_type && (
                      <div className="flex items-center gap-3">
                        <FiBriefcase className="text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">Type de client</p>
                          <div className="flex items-center gap-2">
                            <Badge className={selectedReservation.client_type === 'entreprise' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                              {selectedReservation.client_type === 'entreprise' ? 'Entreprise / Association' : 'Particulier'}
                            </Badge>
                            {selectedReservation.company_name && (
                              <span className="text-sm font-medium">{selectedReservation.company_name}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Modes de paiement recommandés (visible uniquement en back-office) */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-xs text-amber-700 font-medium mb-2">💳 Modes de paiement acceptés</p>
                      {selectedReservation.client_type === 'entreprise' ? (
                        <div className="flex flex-wrap gap-2">
                          <Badge className="bg-blue-100 text-blue-800">Virement bancaire</Badge>
                          <Badge className="bg-purple-100 text-purple-800">Chèque</Badge>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <Badge className="bg-green-100 text-green-800">Virement bancaire (MyPOS)</Badge>
                          <Badge className="bg-indigo-100 text-indigo-800">CB via MyPOS</Badge>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <FiMail className="text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <p className="font-medium">{selectedReservation.client_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <FiPhone className="text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Téléphone</p>
                        <p className="font-medium">{selectedReservation.client_phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <FiMapPin className="text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Adresse événement</p>
                        <p className="font-medium">{selectedReservation.event_address}</p>
                      </div>
                    </div>
                  </div>
                  {selectedReservation.notes && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-xs text-slate-500 mb-1">Notes</p>
                      <p className="text-sm">{selectedReservation.notes}</p>
                    </div>
                  )}
                </TabsContent>

                {/* Onglet Équipe */}
                <TabsContent value="team" className="space-y-4 mt-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                      <FiUsers className="text-indigo-500" />
                      Équipe assignée
                    </h4>
                  </div>
                  
                  {/* Membres actuellement assignés */}
                  {selectedReservation.assigned_team?.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {selectedReservation.assigned_team.map((memberId) => {
                        const member = teamMembers.find(m => m.id === memberId);
                        return member ? (
                          <Badge key={memberId} className="bg-indigo-100 text-indigo-700 py-1 px-3">
                            {member.name} - {member.role}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">Aucun membre assigné</p>
                  )}
                  
                  {/* Liste des membres disponibles */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <p className="text-sm font-medium text-slate-700">Sélectionner les membres :</p>
                    
                    {teamMembers.length === 0 ? (
                      <div className="text-center py-4">
                        <FiUsers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Aucun membre d'équipe</p>
                        <Button 
                          variant="link" 
                          size="sm" 
                          onClick={() => navigate('/dashboard/team')}
                          className="mt-2"
                        >
                          Ajouter des membres
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {teamMembers.map((member) => (
                          <label 
                            key={member.id} 
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedTeamMembers.includes(member.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedTeamMembers([...selectedTeamMembers, member.id]);
                                } else {
                                  setSelectedTeamMembers(selectedTeamMembers.filter(id => id !== member.id));
                                }
                              }}
                            />
                            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {member.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium">{member.name}</p>
                              <p className="text-xs text-slate-500">{member.role}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                    
                    {teamMembers.length > 0 && (
                      <Button 
                        onClick={() => handleAssignTeam(selectedReservation.id)}
                        disabled={savingTeam || selectedTeamMembers.length === 0}
                        className="w-full mt-3"
                      >
                        {savingTeam ? (
                          <span className="flex items-center gap-2">
                            <span className="animate-spin">⏳</span> Enregistrement...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <FiCheck /> Enregistrer l'équipe ({selectedTeamMembers.length})
                          </span>
                        )}
                      </Button>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="actions" className="space-y-4 mt-4">
                  {/* Indicateur de statut clair */}
                  <div className="p-3 rounded-lg bg-slate-50 border">
                    <p className="text-sm font-medium mb-1">Statut actuel :</p>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      selectedReservation.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                      selectedReservation.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                      selectedReservation.status === 'contract_signed' ? 'bg-purple-100 text-purple-800' :
                      selectedReservation.status === 'deposit_paid' ? 'bg-green-100 text-green-800' :
                      selectedReservation.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                      selectedReservation.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-slate-100 text-slate-800'
                    }`}>
                      {selectedReservation.status === 'pending' ? '⏳ En attente de traitement' :
                       selectedReservation.status === 'approved' ? '✅ Acceptée - En attente de l\'acompte' :
                       selectedReservation.status === 'confirmed' ? '💰 Acompte payé - Réservation confirmée' :
                       selectedReservation.status === 'contract_signed' ? '📝 Contrat signé' :
                       selectedReservation.status === 'deposit_paid' ? '💰 Acompte payé' :
                       selectedReservation.status === 'completed' ? '🎉 Terminée' :
                       selectedReservation.status === 'rejected' ? '❌ Refusée' :
                       selectedReservation.status === 'cancelled' ? '🚫 Annulée' :
                       selectedReservation.status}
                    </span>
                  </div>

                  {selectedReservation.status === 'pending' && (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-600 font-medium">Actions disponibles :</p>
                      <div className="flex gap-3">
                        <Button 
                          className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                          onClick={() => handleStatusUpdate(selectedReservation.id, 'approved')}
                          data-testid="approve-reservation-btn"
                        >
                          <FiCheck className="mr-2" />
                          ✅ Accepter la demande
                        </Button>
                        <Button 
                          variant="outline"
                          className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleStatusUpdate(selectedReservation.id, 'rejected')}
                          data-testid="reject-reservation-btn"
                        >
                          <FiX className="mr-2" />
                          ❌ Refuser
                        </Button>
                      </div>
                    </div>
                  )}

                  {selectedReservation.status === 'approved' && !selectedReservation.deposit_paid && (
                    <div className="space-y-4">
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800 font-medium">💡 La réservation est acceptée !</p>
                        <p className="text-xs text-blue-600 mt-1">Vous pouvez maintenant envoyer un lien de paiement au client ou marquer l'acompte comme reçu.</p>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">📤 Envoyer un lien de paiement au client :</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {/* Stripe Payment Link */}
                          <Button
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={async () => {
                              try {
                                const response = await stripeAPI.createCheckout(
                                  selectedReservation.id, 
                                  'deposit'
                                );
                                
                                if (response.data?.checkout_url) {
                                  await navigator.clipboard.writeText(response.data.checkout_url);
                                  toast.success(`Lien Stripe copié ! Envoyez-le au client.`);
                                }
                              } catch (error) {
                                toast.error('Erreur Stripe: ' + (error.response?.data?.detail || error.message));
                              }
                            }}
                          >
                            <FiCreditCard className="mr-2" />
                            Lien Stripe
                          </Button>
                          
                          {/* MyPOS IPC Payment - Dynamic deposit 30% */}
                          <Button
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={async () => {
                              try {
                                const response = await fetch(`${API_URL}/api/payments/mypos-ipc/reservation/${selectedReservation.id}/pay-deposit`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                  }
                                });
                                const data = await response.json();
                                if (data.success && data.checkout_url) {
                                  // Build the checkout URL with form params
                                  const formParams = new URLSearchParams(data.form_params).toString();
                                  const fullCheckoutURL = `${data.checkout_url}?${formParams}`;
                                  await navigator.clipboard.writeText(fullCheckoutURL);
                                  toast.success(`Lien acompte ${formatCurrency(data.amount)} ${paymentSettings?.deposit_type === 'fixed_link' ? '(fixe)' : `(${paymentSettings?.deposit_percentage || 30}%)`} copié ! Envoyez-le au client.`);
                                  loadReservations();
                                  loadFinancialStats();
                                } else {
                                  toast.error(data.detail || 'Erreur création lien MyPOS');
                                }
                              } catch (error) {
                                toast.error('Erreur MyPOS: ' + error.message);
                              }
                            }}
                            data-testid="mypos-deposit-link-btn"
                          >
                            <FiDollarSign className="mr-2" />
                            Lien MyPOS ({formatCurrency(calculateDeposit(selectedReservation.total_price || selectedReservation.total_cost || 0))})
                          </Button>
                        </div>
                      </div>
                      
                      <div className="pt-3 border-t">
                        <p className="text-sm font-medium text-slate-700 mb-2">✅ Ou marquer l'acompte comme reçu :</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {['virement', 'espèces', 'chèque', 'autre'].map((method) => (
                            <Button
                              key={method}
                              variant="outline"
                              size="sm"
                              onClick={() => handlePaymentMark(selectedReservation.id, method)}
                              data-testid={`mark-payment-${method}`}
                            >
                              {method.charAt(0).toUpperCase() + method.slice(1)}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedReservation.status === 'approved' && selectedReservation.deposit_paid && (
                    <div className="space-y-4">
                      {/* Balance Payment Section */}
                      {selectedReservation.payment_status !== 'fully_paid' && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-800 font-medium">💰 Acompte reçu !</p>
                          <p className="text-xs text-green-600 mt-1">
                            Après la prestation, envoyez le lien de paiement du solde au client.
                          </p>
                          <p className="text-sm font-medium text-green-700 mt-2">
                            Solde restant: {formatCurrency(calculateBalance(selectedReservation))}
                          </p>
                          <div className="flex flex-col sm:flex-row gap-2 mt-3">
                            <Button
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                              onClick={async () => {
                                try {
                                  const response = await fetch(`${API_URL}/api/payments/mypos-ipc/reservation/${selectedReservation.id}/pay-balance`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                                    }
                                  });
                                  const data = await response.json();
                                  if (data.success && data.checkout_url) {
                                    // Copy checkout URL for client
                                    const checkoutURL = `${data.checkout_url}?${new URLSearchParams(data.form_params).toString()}`;
                                    await navigator.clipboard.writeText(checkoutURL);
                                    toast.success(`Lien solde (${formatCurrency(data.amount)}) copié !`);
                                    loadReservations();
                                    loadFinancialStats();
                                  } else {
                                    toast.error(data.detail || data.error || 'Erreur création lien solde');
                                  }
                                } catch (error) {
                                  toast.error('Erreur: ' + error.message);
                                }
                              }}
                              data-testid="mypos-balance-link-btn"
                            >
                              <FiCreditCard className="mr-2" />
                              Lien paiement solde
                            </Button>
                            
                            {/* Commercial Gesture Button - Admin only */}
                            {user?.role === 'admin' && (
                              <Button
                                variant="outline"
                                className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                                onClick={() => {
                                  if (window.confirm(`Confirmer le geste commercial ?\nCela annulera le solde de ${formatCurrency(calculateBalance(selectedReservation))} et marquera la réservation comme soldée.`)) {
                                    handleGesteCommercial(selectedReservation.id, 'Geste commercial');
                                  }
                                }}
                                data-testid="geste-commercial-btn"
                              >
                                <FiGift className="mr-2" />
                                Geste commercial
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {selectedReservation.payment_status === 'fully_paid' && (
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <p className="text-sm text-emerald-800 font-medium flex items-center gap-2">
                            <FiCheck className="h-4 w-4" />
                            Réservation entièrement payée !
                            {selectedReservation.geste_commercial && (
                              <Badge className="ml-2 bg-amber-100 text-amber-700">
                                <FiGift className="mr-1 h-3 w-3" />
                                Geste commercial
                              </Badge>
                            )}
                          </p>
                          {selectedReservation.geste_commercial && (
                            <p className="text-xs text-emerald-600 mt-1">
                              Solde de {formatCurrency(selectedReservation.geste_commercial_amount || 0)} offert
                            </p>
                          )}
                        </div>
                      )}
                      
                      <Button 
                        className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
                        onClick={() => handleStatusUpdate(selectedReservation.id, 'completed')}
                        data-testid="complete-reservation-btn"
                      >
                        <FiCheck className="mr-2" />
                        Marquer comme terminée
                      </Button>
                    </div>
                  )}

                  {/* Design Validation Section */}
                  {(selectedReservation.status === 'approved' || selectedReservation.status === 'confirmed') && (
                    <div className="border-t border-slate-100 pt-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-slate-900 flex items-center gap-2">
                          <FiImage className="h-4 w-4" />
                          Validation du design
                        </h4>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          selectedReservation.design_status === 'approved' 
                            ? 'bg-emerald-100 text-emerald-700'
                            : selectedReservation.design_status === 'pending'
                            ? 'bg-orange-100 text-orange-700'
                            : selectedReservation.design_status === 'revision_requested'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {selectedReservation.design_status === 'approved' ? '✓ Validé par le client'
                            : selectedReservation.design_status === 'pending' ? '⏳ En attente de validation'
                            : selectedReservation.design_status === 'revision_requested' ? '✏️ Modifications demandées'
                            : '📤 À envoyer'}
                        </span>
                      </div>
                      
                      {/* Messages d'échange */}
                      {selectedReservation.design_messages?.length > 0 && (
                        <div className="max-h-40 overflow-y-auto space-y-2 bg-slate-50 rounded-lg p-3">
                          {selectedReservation.design_messages.map((msg, i) => (
                            <div key={i} className={`flex ${msg.from === 'admin' ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                                msg.from === 'admin' 
                                  ? 'bg-indigo-500 text-white' 
                                  : 'bg-white border border-slate-200'
                              }`}>
                                <p>{msg.text}</p>
                                {msg.attachment && (
                                  <a href={msg.attachment} target="_blank" rel="noopener noreferrer" className="text-xs underline flex items-center gap-1 mt-1">
                                    <FiPaperclip className="h-3 w-3" /> Pièce jointe
                                  </a>
                                )}
                                <span className="text-xs opacity-60 block mt-1">{msg.date}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Formulaire d'envoi de design */}
                      <DesignSendForm 
                        reservation={selectedReservation}
                        onSend={handleSendDesign}
                      />
                    </div>
                  )}

                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-sm text-slate-600 mb-3">Exporter la fiche de location:</p>
                    <div className="flex gap-3">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleDownloadPDF(selectedReservation)}
                        data-testid="export-pdf-btn"
                      >
                        <FiFileText className="mr-2" />
                        Télécharger PDF
                      </Button>
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleExportExcel(selectedReservation.id)}
                        data-testid="export-excel-btn"
                      >
                        <FiDownload className="mr-2" />
                        Excel
                      </Button>
                    </div>
                  </div>

                  {/* Link to detailed status management page */}
                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-sm text-slate-600 mb-3">Gestion avancée:</p>
                    <Button 
                      className="w-full bg-slate-800 hover:bg-slate-700 text-white"
                      onClick={() => {
                        setDialogOpen(false);
                        navigate(`/dashboard/reservations/${selectedReservation.id}`);
                      }}
                      data-testid="view-full-detail-btn"
                    >
                      <FiEye className="mr-2" />
                      Voir la fiche complète & Gérer les statuts
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <FiAlertTriangle className="h-5 w-5" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          
          {reservationToDelete && (
            <div className="space-y-4">
              <p className="text-slate-600">
                Êtes-vous sûr de vouloir supprimer cette réservation ?
              </p>
              
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{reservationToDelete.client_name}</p>
                <p className="text-sm text-slate-500">{reservationToDelete.product_name}</p>
                <p className="text-sm text-slate-500">
                  {new Date(reservationToDelete.event_date).toLocaleDateString('fr-FR')}
                </p>
              </div>
              
              <p className="text-sm text-red-600">
                Cette action est irréversible. Tous les designs, contrats et factures associés seront également supprimés.
              </p>
              
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteDialogOpen(false);
                    setReservationToDelete(null);
                  }}
                >
                  Annuler
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteReservation}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-reservation"
                >
                  {deleting ? 'Suppression...' : 'Supprimer'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Composant pour envoyer un design au client
function DesignSendForm({ reservation, onSend }) {
  const [message, setMessage] = useState('');
  const [designType, setDesignType] = useState('image'); // 'image' ou 'link'
  const [designUrl, setDesignUrl] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message && !designUrl) {
      toast.error('Veuillez ajouter un message ou un design');
      return;
    }
    setSending(true);
    try {
      await onSend(reservation.id, {
        message,
        design_type: designType,
        design_url: designUrl,
        send_email: true
      });
      setMessage('');
      setDesignUrl('');
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 bg-slate-50 rounded-lg p-4">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setDesignType('image')}
          className={`flex-1 py-2 px-3 text-sm rounded-lg border transition-colors ${
            designType === 'image' 
              ? 'bg-indigo-500 text-white border-indigo-500' 
              : 'bg-white border-slate-200 hover:border-indigo-300'
          }`}
        >
          <FiImage className="inline mr-1 h-4 w-4" /> Image
        </button>
        <button
          type="button"
          onClick={() => setDesignType('link')}
          className={`flex-1 py-2 px-3 text-sm rounded-lg border transition-colors ${
            designType === 'link' 
              ? 'bg-indigo-500 text-white border-indigo-500' 
              : 'bg-white border-slate-200 hover:border-indigo-300'
          }`}
        >
          <FiLink className="inline mr-1 h-4 w-4" /> Lien vidéo
        </button>
      </div>
      
      <Input
        type="url"
        value={designUrl}
        onChange={(e) => setDesignUrl(e.target.value)}
        placeholder={designType === 'image' ? "URL de l'image du design" : "URL de la vidéo (YouTube, Vimeo...)"}
      />
      
      <Textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Message pour le client (ex: Voici le design proposé pour votre événement...)"
        rows={2}
      />
      
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Un email sera envoyé au client pour validation
        </p>
        <Button type="submit" size="sm" disabled={sending}>
          <FiSend className="mr-1 h-3 w-3" />
          {sending ? 'Envoi...' : 'Envoyer'}
        </Button>
      </div>
    </form>
  );
}
